import { faSearch } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { Template } from '@feathr/blackbox';
import { TemplateClass, templateTypeLabel } from '@feathr/blackbox';
import type { IUniversalSelectOption } from '@feathr/components';
import { DebouncedInput, Input, Table, UniversalSelect } from '@feathr/components';
import { useAccount, useStore } from '@feathr/extender/state';

import EventTemplatesPage from '../EventTemplatesPage';
import eventTemplatesCampaignColumns from './eventTemplatesCampaignColumns';
import { formatTemplateTypeOptionLabel } from './EventTemplatesCampaignPage.formatters';

import * as styles from './EventTemplatesCampaignPage.css';

interface IFilters {
  name__icontains?: string;
  _cls?: TemplateClass;
  _cls__nin: TemplateClass[];
}

function EventTemplatesCampaignPage(): JSX.Element {
  const account = useAccount();
  const { Templates } = useStore();
  const { eventId } = useParams<{ eventId: string }>();
  const [filters, setFilters] = useState<IFilters>({
    _cls__nin: account?.isFinchLegacy
      ? [TemplateClass.Link]
      : [
          TemplateClass.Link,
          TemplateClass.ReferralPage,
          TemplateClass.ReferralEmail,
          TemplateClass.ReferralBanner,
        ],
  });
  const { t } = useTranslation();

  function handleDebouncedSearchChange(newValue?: string) {
    setFilters({
      ...filters,
      name__icontains: newValue,
    });
  }

  function filterOption({ name }: IUniversalSelectOption, inputValue: string): boolean {
    return name.toLowerCase().includes(inputValue.toLowerCase());
  }

  function onClearType() {
    setFilters({
      ...filters,
      _cls: undefined,
    });
  }

  function onSelectType({ id }: IUniversalSelectOption): void {
    setFilters({
      ...filters,
      _cls: id as TemplateClass,
    });
  }

  const typeOptions = Object.keys(TemplateClass)
    // Filter out account-level template options.
    .filter((key) => !['Email', 'Page', 'Link', 'Banner'].includes(key as TemplateClass))
    .filter((key) => {
      if (account && !account.isFinchLegacy) {
        return !(key.includes('Referral') || key.includes('Partner'));
      }
      return true;
    })
    .map<IUniversalSelectOption>((key: string) => {
      const value = TemplateClass[key as keyof typeof TemplateClass];
      return {
        id: value,
        name: templateTypeLabel(value),
      };
    })
    .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));

  const selectedType = typeOptions.find((option) => option.id === filters._cls);

  const filterElements = [
    <UniversalSelect<IUniversalSelectOption>
      filterOption={filterOption}
      formatOptionLabel={formatTemplateTypeOptionLabel}
      isClearable={true}
      key={'template-type'}
      name={'template-type'}
      onClear={onClearType}
      onSelectSingle={onSelectType}
      options={typeOptions}
      placeholder={t('Type')}
      value={selectedType}
      wrapperClassName={styles.filter}
    />,
    <DebouncedInput<string>
      defaultValue={filters.name__icontains}
      key={'template-search'}
      onChange={handleDebouncedSearchChange}
    >
      {(liveValue, onChangeLiveValue) => (
        <Input
          isClearable={true}
          onChange={onChangeLiveValue}
          placeholder={'Search by name...'}
          prefix={<FontAwesomeIcon icon={faSearch} />}
          type={'text'}
          value={liveValue}
        />
      )}
    </DebouncedInput>,
  ];

  return (
    <EventTemplatesPage sectionTitle={t('Templates')} title={t('From Campaigns')}>
      <Table<Template>
        collection={Templates}
        columns={eventTemplatesCampaignColumns()}
        filterElements={filterElements}
        filters={{
          ...filters,
          default_neq: true,
          event: eventId,
          account: account.id,
          parent_kind: 'campaign',
        }}
        initialSort={[{ id: 'date_last_modified', desc: true }]}
      />
    </EventTemplatesPage>
  );
}

export default observer(EventTemplatesCampaignPage);
